import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const LocationsManagement = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [newLocation, setNewLocation] = useState({
    name: '',
    type: 'Branch',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    phone: '',
    email: '',
    isDefault: false,
    status: 'Active'
  });

  const locationTypes = ['Primary', 'Branch', 'Warehouse', 'Store', 'Office'];
  const statusOptions = ['Active', 'Inactive', 'Closed'];

  // TODO: Load locations from database
  React.useEffect(() => {
    const loadLocations = async () => {
      try {
        setLoading(true);
        // TODO: Replace with actual API call to load locations
        // const data = await locationService.getAll();
        // setLocations(data);
        setLocations([]);
      } catch (err) {
        setError(err?.message || 'Failed to load locations');
      } finally {
        setLoading(false);
      }
    };

    loadLocations();
  }, []);

  const handleAddLocation = () => {
    const location = {
      ...newLocation,
      id: Math.max(...(locations?.map(l => l?.id) || [0]), 0) + 1
    };
    setLocations(prev => [...prev, location]);
    setNewLocation({
      name: '',
      type: 'Branch',
      address: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      phone: '',
      email: '',
      isDefault: false,
      status: 'Active'
    });
    setShowAddModal(false);
  };

  const handleEditLocation = (location) => {
    setEditingLocation(location);
    setNewLocation(location);
    setShowAddModal(true);
  };

  const handleUpdateLocation = () => {
    setLocations(prev => 
      prev?.map(location => 
        location?.id === editingLocation?.id ? newLocation : location
      )
    );
    setEditingLocation(null);
    setNewLocation({
      name: '',
      type: 'Branch',
      address: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      phone: '',
      email: '',
      isDefault: false,
      status: 'Active'
    });
    setShowAddModal(false);
  };

  const handleDeleteLocation = (locationId) => {
    setLocations(prev => prev?.filter(location => location?.id !== locationId));
  };

  const setAsDefault = (locationId) => {
    setLocations(prev => 
      prev?.map(location => ({
        ...location,
        isDefault: location?.id === locationId
      }))
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Icon name="Loader2" size={24} className="animate-spin mr-2" />
        <span>Loading locations...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center space-x-2">
          <Icon name="AlertCircle" size={16} className="text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Locations Management</h2>
          <p className="text-muted-foreground">Manage your organization's business locations and addresses</p>
        </div>
        <Button onClick={() => setShowAddModal(true)} iconName="Plus">
          Add Location
        </Button>
      </div>

      {/* Locations List */}
      <div className="space-y-4">
        {locations?.length === 0 ? (
          <div className="bg-surface border border-border rounded-lg p-8 text-center">
            <div className="flex flex-col items-center space-y-3">
              <Icon name="MapPin" size={48} className="text-muted-foreground/50" />
              <h3 className="text-lg font-medium text-foreground">No locations found</h3>
              <p className="text-muted-foreground">Start by adding your first business location</p>
              <Button onClick={() => setShowAddModal(true)} iconName="Plus">
                Add Location
              </Button>
            </div>
          </div>
        ) : (
          locations?.map((location) => (
            <div key={location?.id} className="bg-surface border border-border rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${
                    location?.status === 'Active' ? 'bg-green-500' : 
                    location?.status === 'Inactive' ? 'bg-yellow-500' : 'bg-red-500'
                  }`} />
                  <div>
                    <h3 className="text-lg font-medium text-foreground">{location?.name}</h3>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        location?.type === 'Primary' ? 'bg-primary/10 text-primary' : 'bg-muted text-foreground'
                      }`}>
                        {location?.type}
                      </span>
                      {location?.isDefault && (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                          Default
                        </span>
                      )}
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        location?.status === 'Active' ? 'bg-green-100 text-green-700' 
                          : location?.status === 'Inactive' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {location?.status}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex space-x-2">
                  {!location?.isDefault && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAsDefault(location?.id)}
                      iconName="Star"
                    >
                      Set as Default
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditLocation(location)}
                    iconName="Edit"
                  >
                    Edit
                  </Button>
                  {!location?.isDefault && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteLocation(location?.id)}
                      iconName="Trash2"
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-2">Address</h4>
                  <p className="text-sm text-muted-foreground">
                    {location?.address}<br />
                    {location?.city}, {location?.state} {location?.postalCode}<br />
                    {location?.country}
                  </p>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-2">Contact</h4>
                  <p className="text-sm text-muted-foreground">
                    {location?.phone && (
                      <>
                        <Icon name="Phone" size={12} className="inline mr-1" />
                        {location?.phone}<br />
                      </>
                    )}
                    {location?.email && (
                      <>
                        <Icon name="Mail" size={12} className="inline mr-1" />
                        {location?.email}
                      </>
                    )}
                  </p>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-foreground mb-2">Quick Actions</h4>
                  <div className="flex space-x-2">
                    <Button variant="ghost" size="sm" iconName="MapPin">
                      View Map
                    </Button>
                    <Button variant="ghost" size="sm" iconName="ExternalLink">
                      Visit
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Location Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">
                {editingLocation ? 'Edit Location' : 'Add New Location'}
              </h3>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => {
                  setShowAddModal(false);
                  setEditingLocation(null);
                  setNewLocation({
                    name: '',
                    type: 'Branch',
                    address: '',
                    city: '',
                    state: '',
                    postalCode: '',
                    country: '',
                    phone: '',
                    email: '',
                    isDefault: false,
                    status: 'Active'
                  });
                }}
              >
                <Icon name="X" size={16} />
              </Button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[70vh] space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Location Name"
                  value={newLocation?.name}
                  onChange={(e) => setNewLocation(prev => ({ ...prev, name: e?.target?.value }))}
                  placeholder="Enter location name"
                  required
                />
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Location Type
                  </label>
                  <select
                    value={newLocation?.type}
                    onChange={(e) => setNewLocation(prev => ({ ...prev, type: e?.target?.value }))}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    {locationTypes?.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Address Information */}
              <div className="space-y-4">
                <Input
                  label="Street Address"
                  value={newLocation?.address}
                  onChange={(e) => setNewLocation(prev => ({ ...prev, address: e?.target?.value }))}
                  placeholder="Enter street address"
                />
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Input
                    label="City"
                    value={newLocation?.city}
                    onChange={(e) => setNewLocation(prev => ({ ...prev, city: e?.target?.value }))}
                    placeholder="Enter city"
                  />
                  <Input
                    label="State/Province"
                    value={newLocation?.state}
                    onChange={(e) => setNewLocation(prev => ({ ...prev, state: e?.target?.value }))}
                    placeholder="Enter state"
                  />
                  <Input
                    label="Postal Code"
                    value={newLocation?.postalCode}
                    onChange={(e) => setNewLocation(prev => ({ ...prev, postalCode: e?.target?.value }))}
                    placeholder="Enter postal code"
                  />
                  <Input
                    label="Country"
                    value={newLocation?.country}
                    onChange={(e) => setNewLocation(prev => ({ ...prev, country: e?.target?.value }))}
                    placeholder="Enter country"
                  />
                </div>
              </div>

              {/* Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Phone"
                  value={newLocation?.phone}
                  onChange={(e) => setNewLocation(prev => ({ ...prev, phone: e?.target?.value }))}
                  placeholder="+1 (555) 123-4567"
                />
                <Input
                  label="Email"
                  type="email"
                  value={newLocation?.email}
                  onChange={(e) => setNewLocation(prev => ({ ...prev, email: e?.target?.value }))}
                  placeholder="location@company.com"
                />
              </div>

              {/* Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Status
                  </label>
                  <select
                    value={newLocation?.status}
                    onChange={(e) => setNewLocation(prev => ({ ...prev, status: e?.target?.value }))}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    {statusOptions?.map((status) => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newLocation?.isDefault}
                      onChange={(e) => setNewLocation(prev => ({ ...prev, isDefault: e?.target?.checked }))}
                      className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    />
                    <span className="text-sm font-medium text-foreground">Set as default location</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4 p-6 border-t border-border">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowAddModal(false);
                  setEditingLocation(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={editingLocation ? handleUpdateLocation : handleAddLocation}
                iconName="Save"
              >
                {editingLocation ? 'Update Location' : 'Add Location'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationsManagement;